import { omitDeep } from 'deepdash/standalone';
import { flow } from 'fp-ts/lib/function';
import {
  DAYS_OF_THE_WEEK,
  DEFAULT_CAMPAIGN_RECURRENCE_FREQUENCY,
  DEFAULT_PRE_CREATE_RECURRING_CAMPAIGN_HOURS,
  MAX_CAMPAIGN_SCHEDULE_AHEAD_DAYS,
  MIN_RECURRING_CAMPAIGN_END_ON_DAYS,
  MIN_SCHEDULE_TIME_HOURS_IN_THE_FUTURE,
  RECURRENCE_SCHEDULE_MAPPER,
  SUPPORTED_CAMPAIGN_SCHEDULING_MOMENT_TIME_FORMATS,
} from 'modules/campaigns/lib/constants';
import { GraphQLCampaign, RecurrenceScheduleType } from 'modules/campaigns/lib/types';
import {
  getCampaignEndRecurrenceType,
  getCampaignRecurrenceScheduleType,
  getIsRecurringScheduleType,
  skipDaysMapperToList,
} from 'modules/campaigns/lib/utils';
import { DataScienceOptimization, Day, QuietHours, ScheduleType } from 'modules/domains/campaign';
import moment, { Moment, unitOfTime } from 'moment';
import momentTz from 'moment-timezone';
import { match } from 'ts-pattern';
import { TimeIn12HourClock, TimeIn24HourClock } from 'ui/datePicker';
import { parseISOZonedDate } from 'utils/date';
import { Nullable } from 'utils/tsHelpers';
import {
  EndEligibilityType,
  EndRecurrenceType,
  ScheduleEligibilityFormState,
  ScheduleFormState,
} from '../../types';
import { roundMomentToNearestInterval } from '../../utils';
import { RECURRENCE_SCHEDULE_TYPES_WITH_SKIP_ON_DAYS } from './ScheduleCampaignModal.constants';

/**
 * Returns target time zone based on schedule type
 */
export const getScheduleCampaignTargetTimeZone = (
  projectTimeZone: string,
  startingTimeZone?: Nullable<string>,
  scheduleType?: ScheduleType,
) => {
  return match(scheduleType)
    .with(ScheduleType.RecipientTimeZone, () => startingTimeZone || projectTimeZone)
    .otherwise(() => projectTimeZone);
};

/**
 * Returns a moment object of the current time in the target time zone
 */
const getCurrentTimeInTargetTimeZone = flow(getScheduleCampaignTargetTimeZone, (tz) =>
  momentTz.tz(tz),
);

/**
 * Returns whether the schedule time in is in the future for the target time zone
 */
export const getIsStartDateInTheFuture = ({
  startDate,
  scheduleType,
  projectTimeZone,
  startingTimeZone,
}: Pick<
  Partial<ScheduleFormState>,
  'startDate' | 'scheduleType' | 'projectTimeZone' | 'startingTimeZone'
>) => {
  if (startDate && projectTimeZone) {
    const currentTimeInTargetTimeZone = getCurrentTimeInTargetTimeZone(
      projectTimeZone,
      startingTimeZone,
      scheduleType,
    );

    const targetTimeZone = getScheduleCampaignTargetTimeZone(
      projectTimeZone,
      startingTimeZone,
      scheduleType,
    );

    const format = 'YYYY-MM-DD hh:mm A';
    const scheduleDate = momentTz
      .tz(startDate.format(format), format, targetTimeZone)
      .startOf('day'); // same date but in the target time zone
    const todayInTargetTimeZone = currentTimeInTargetTimeZone?.clone().startOf('day');

    return scheduleDate?.isSameOrAfter(todayInTargetTimeZone);
  }

  return true;
};

/**
 * Returns whether the schedule time in is in the future for the target time zone
 */
export const getIsStartTimeInTheFuture = ({
  startTime,
  startDate,
  projectTimeZone,
  startingTimeZone,
  scheduleType,
}: Pick<
  Partial<ScheduleFormState>,
  'startTime' | 'startDate' | 'projectTimeZone' | 'startingTimeZone' | 'scheduleType'
>) => {
  if (startTime && startDate && projectTimeZone) {
    const targetTimeZone = getScheduleCampaignTargetTimeZone(
      projectTimeZone,
      startingTimeZone,
      scheduleType,
    );

    const currentTimeInTargetTimeZone = getCurrentTimeInTargetTimeZone(
      projectTimeZone,
      startingTimeZone,
      scheduleType,
    );

    const dateTime = `${startDate?.format('YYYY-MM-DD')} ${startTime}`; // date string

    const scheduleDateTimeInTargetTimeZone = momentTz.tz(
      dateTime,
      SUPPORTED_CAMPAIGN_SCHEDULING_MOMENT_TIME_FORMATS,
      targetTimeZone,
    );

    return scheduleDateTimeInTargetTimeZone?.isAfter(currentTimeInTargetTimeZone);
  }

  return true;
};

/**
 * Returns whether the given time string is a valid format.
 *
 * 12 and 24-hour formats are supported.
 */
export const getIsTimeValid = (time: string) => {
  const re = /^(([0-2])?[0-9]:[0-5][0-9](\s(PM|pm|AM|am))?)$/;
  return re.test(time);
};

export const getIsEndDateAfterStartDate = ({
  startDate,
  endDate,
  endType,
  scheduleType,
  projectTimeZone,
  startingTimeZone,
}: Pick<
  Partial<ScheduleFormState | ScheduleEligibilityFormState>,
  'startDate' | 'endDate' | 'endType' | 'scheduleType' | 'projectTimeZone' | 'startingTimeZone'
>) => {
  if (
    startDate &&
    endDate &&
    projectTimeZone &&
    (endType === EndRecurrenceType.OnDate || endType === EndEligibilityType.OnDate)
  ) {
    const targetTimeZone = getScheduleCampaignTargetTimeZone(
      projectTimeZone,
      startingTimeZone,
      scheduleType,
    );

    const format = 'YYYY-MM-DD hh:mm A';
    const scheduleStartDate = momentTz
      .tz(startDate?.format(format), format, targetTimeZone)
      .startOf('day'); // same date but in the target time zone
    const scheduleEndDate = momentTz
      .tz(endDate?.format(format), format, targetTimeZone)
      .startOf('day'); // same date but in the target time zone

    return scheduleEndDate?.isAfter(scheduleStartDate);
  }

  return true;
};

export const getIsEndDateAfterStartDateOrNow = ({
  startDate,
  endDate,
  endType,
  scheduleType,
  projectTimeZone,
  startingTimeZone,
}: Parameters<typeof getIsEndDateAfterStartDate>[0]) =>
  getIsEndDateAfterStartDate({
    startDate: startDate ?? moment(),
    endDate,
    endType,
    scheduleType,
    projectTimeZone,
    startingTimeZone,
  });

export const getIsEndAfterValid = ({
  endType,
  endAfterValue,
}: Pick<Partial<ScheduleEligibilityFormState>, 'startTime' | 'endAfterValue' | 'endType'>) => {
  if (endType === EndEligibilityType.After && typeof endAfterValue === 'number') {
    return endAfterValue > 0;
  }
  return true;
};

const epochToLocalMoment = (epochMs: any, timeZone: string) => {
  const rawDate = momentTz.tz(epochMs, timeZone);

  // Convert date & time to local time set by browser
  return moment()
    .year(rawDate.year())
    .month(rawDate.month())
    .date(rawDate.date())
    .hour(rawDate.hour())
    .minute(rawDate.minute())
    .second(0)
    .millisecond(0);
};

export const getCampaignScheduleDateRange = (timeZone: string) => {
  const currDate = epochToLocalMoment(moment().valueOf(), timeZone).startOf('day');

  return {
    start: currDate,
    end: currDate.clone().add(MAX_CAMPAIGN_SCHEDULE_AHEAD_DAYS, 'days'),
  };
};

export const getCampaignRecurringEndOnDateRange = (timeZone: string) => {
  const currDate = epochToLocalMoment(moment().valueOf(), timeZone).startOf('day');

  return {
    start: currDate.clone().add(MIN_RECURRING_CAMPAIGN_END_ON_DAYS, 'days'),
  };
};

/**
 * Returns schedule campaign startTime(and date) in milliseconds
 */
export const getStartTimeInEpochMs = (
  startDate: Moment,
  startTime: TimeIn12HourClock | TimeIn24HourClock | string,
  targetTimeZone: string,
) => {
  const scheduleDateTime = `${startDate.format('YYYY-MM-DD')} ${startTime}`;
  const startTimeAndDateInMs = momentTz(
    scheduleDateTime,
    SUPPORTED_CAMPAIGN_SCHEDULING_MOMENT_TIME_FORMATS,
  )
    .tz(targetTimeZone, true)
    ?.valueOf(); // Convert start date & time to local time set by browser

  return startTimeAndDateInMs;
};

/**
 * Convert `skipDays`(array) to `skipDaysOfWeek`(object)
 */
export const getSkipDaysOfWeek = (skipDays: Day[], recurrence: RecurrenceScheduleType) => {
  const skipDaysSet = new Set(skipDays);
  const isRecurrenceWithSkipDays = RECURRENCE_SCHEDULE_TYPES_WITH_SKIP_ON_DAYS.includes(recurrence);

  const skipDaysOfWeek = DAYS_OF_THE_WEEK.reduce(
    (days, day) => {
      const shouldSkip = isRecurrenceWithSkipDays && skipDaysSet.has(day);

      return { ...days, [day]: shouldSkip };
    },
    {} as Record<Day, boolean>,
  );

  return skipDaysOfWeek;
};

/**
 * Returns a recurrence schedule
 * @see https://github.com/Iterable/Iterable/pull/14710#issue-1391134540
 */
export const getRecurrenceSchedule = (
  recurrence: RecurrenceScheduleType,
  customFrequency: string,
) => {
  if (recurrence === RecurrenceScheduleType.Custom) {
    const { period, ...rest } = RECURRENCE_SCHEDULE_MAPPER[recurrence];
    return { ...rest, period: period({ customFrequency }) };
  }
  return RECURRENCE_SCHEDULE_MAPPER?.[recurrence];
};

/**
 * Generates payload for scheduling a campaign based on the schedule modal form and saved campaign data
 */
export const getScheduleCampaignPayload = (
  values: ScheduleFormState,
  {
    projectTimeZone,
    campaign,
    sendSize,
  }: { projectTimeZone: string; campaign: GraphQLCampaign; sendSize?: number },
) => {
  const {
    skipOnDays,
    recurrence,
    recurrenceFrequency,
    precreateHours,
    startTime,
    startDate,
    endDate,
    endType,
    scheduleType,
    startingTimeZone,
    fallbackTimeZone,
  } = values;

  const {
    id,
    customQuietHours,
    customRateLimit,
    schedulerMsgParams,
    dataScienceOptimizations,
    mobileAppIds,
  } = campaign;

  const isSTOEnabled = !!dataScienceOptimizations?.includes(
    DataScienceOptimization.SendTimeOptimization,
  );

  const targetTimeZone = getScheduleCampaignTargetTimeZone(
    projectTimeZone,
    startingTimeZone,
    scheduleType,
  );

  const isRecurring = getIsRecurringScheduleType(recurrence);
  const skipDaysOfWeek = getSkipDaysOfWeek(skipOnDays, recurrence);
  const recurSchedule = getRecurrenceSchedule(recurrence, recurrenceFrequency);

  const startTimeAndDateInMs = getStartTimeInEpochMs(
    startDate,
    startTime as TimeIn12HourClock,
    targetTimeZone,
  );

  const endDateInMs = endDate?.startOf('day')?.valueOf();
  const hasAutomaticSendTime = dataScienceOptimizations?.includes(
    DataScienceOptimization.SendTimeOptimization,
  );

  /**
   * This data is only sent for recurring campaigns
   */
  const recurringCampaignData = {
    recurring: true,
    recurSchedule,
    skipDaysOfWeek,
    precreateHours: parseInt(precreateHours, 10),
    endDate: endType === EndRecurrenceType.OnDate ? endDateInMs : null,
  };

  const payload = {
    sendSize,
    automaticSendTime: !!schedulerMsgParams?.automaticSendTime || hasAutomaticSendTime,
    campaignId: id,
    customRateLimit: customRateLimit ?? null,
    customQuietHours: customQuietHours ?? null,
    dataScienceOptimizations: dataScienceOptimizations ?? [],
    windowHours: schedulerMsgParams?.windowHours ?? null,
    startTime: startTimeAndDateInMs,
    mobileAppIds: mobileAppIds ?? [],
    scheduleType: isSTOEnabled ? DataScienceOptimization.SendTimeOptimization : scheduleType,
    startTimeZone: scheduleType === ScheduleType.RecipientTimeZone ? startingTimeZone : null,
    defaultTimeZone: scheduleType === ScheduleType.ProjectTimeZone ? null : fallbackTimeZone,
    ...(isRecurring && recurringCampaignData),
  };

  return omitDeep(payload, ['__typename']);
};

/**
 * Returns whether a moment date is between a range inclusively
 */
export const getIsMomentBetweenInclusive = (
  date: Moment,
  start: Moment,
  end: Moment,
  precision: unitOfTime.StartOf = 'minute',
) => date.isBetween(start, end, precision, '[]');

/**
 * Returns whether a given Quiet Hours window will overlap with the campaigns send time
 */
export const getIsSendTimeWithinQuietHours = (
  sendTime: string,
  startDate: Moment,
  customQuietHours: QuietHours,
) => {
  const { startTime, endTime } = customQuietHours || {};

  if (!startTime || !endTime) {
    return false;
  }

  const sendTimeStr = `${startDate?.format('YYYY-MM-DD')} ${sendTime}`;
  const scheduleDateTime = moment(sendTimeStr, SUPPORTED_CAMPAIGN_SCHEDULING_MOMENT_TIME_FORMATS);

  const qhStart = scheduleDateTime.clone().startOf('day').add(moment.duration(startTime));
  const qhEnd = scheduleDateTime.clone().startOf('day').add(moment.duration(endTime));

  /**
   * For Quiet Hour windows that ends the next day,
   * we move the end date to the next day.
   * ex: 8:00PM - 9:00AM
   */
  if (qhEnd.isBefore(qhStart)) {
    qhEnd.add(1, 'days');
  }

  /**
   * Checking for qh within the scheduled date and the day after the scheduled date because Quiet Hours may be a window over two days.
   * ex: 8:00PM - 9:00AM
   */
  const isWithinQuietHours =
    getIsMomentBetweenInclusive(scheduleDateTime, qhStart, qhEnd) ||
    getIsMomentBetweenInclusive(scheduleDateTime.clone().add(1, 'days'), qhStart, qhEnd);

  return isWithinQuietHours;
};

/**
 * Generates default values for the scheduling campaign modal form
 * @todo maybe split this function into smaller chunks and move default values generation(when BE data is not available) to a separate function
 * ^^ This would help with making it more transparent values are used and when they're used
 */
export const getScheduleFormInitialValues = (
  campaign: GraphQLCampaign,
  projectTimeZone: string,
): ScheduleFormState => {
  const targetTimeZone = getScheduleCampaignTargetTimeZone(
    projectTimeZone,
    campaign?.schedulerMsgParams?.startTimeZone ?? undefined,
    campaign?.schedulerMsgParams?.scheduleType ?? ScheduleType.ProjectTimeZone,
  );

  /**
   * We use these values when they're available from the BE
   */
  const startDateTime = momentTz(campaign?.startAt).tz(targetTimeZone);
  const startDate = startDateTime?.clone()?.startOf('day');
  const startTime = startDateTime?.clone()?.format('hh:mm A') as TimeIn12HourClock;
  const endDate = parseISOZonedDate(campaign?.schedulerMsgParams?.endDate?.timestamp as string);

  /**
   * Computed default values
   */
  const currentTimeInTargetTimeZone = getCurrentTimeInTargetTimeZone(
    projectTimeZone,
    undefined, // default to project time zone for non-scheduled campaigns, so we don't have a fallback time zone
    ScheduleType.ProjectTimeZone, // default to project time zone for non-scheduled campaigns
  );

  const defaultStartDateTime = roundMomentToNearestInterval(15, currentTimeInTargetTimeZone)
    .clone()
    .add(MIN_SCHEDULE_TIME_HOURS_IN_THE_FUTURE, 'hour');
  const defaultStartTime = defaultStartDateTime.format('hh:mm A') as TimeIn12HourClock;
  const defaultStartDate = defaultStartDateTime.clone().startOf('day');
  const startingDate = campaign?.startAt ? startDate : defaultStartDate;
  const defaultEndDate = startingDate
    .clone()
    .startOf('day')
    .add(MIN_RECURRING_CAMPAIGN_END_ON_DAYS, 'days'); // This has to be based on whichever startDate we end up selecting as default (ex: `startDate` vs `defaultStartDate`)

  /**
   * Returns BE values or default
   */
  const endType = getCampaignEndRecurrenceType(campaign);
  const recurrence = getCampaignRecurrenceScheduleType(campaign);
  const skipOnDays = skipDaysMapperToList(campaign?.schedulerMsgParams?.skipDaysOfWeek);

  return {
    startDate: startingDate,
    startTime: campaign?.startAt ? startTime : defaultStartTime,
    endDate: campaign?.schedulerMsgParams?.endDate?.timestamp ? endDate : defaultEndDate,
    endType,
    projectTimeZone,
    startingTimeZone: campaign?.schedulerMsgParams?.startTimeZone ?? projectTimeZone,
    fallbackTimeZone: campaign?.schedulerMsgParams?.defaultTimeZone ?? projectTimeZone,
    /**
     * Although this can also be set to other values than `ScheduleType.RecipientTimeZone` or `ScheduleType.ProjectTimeZone` (ex: SendTimeOptimization) on the BE,
     * it's always displayed to users as `ScheduleType.RecipientTimeZone` or `ScheduleType.ProjectTimeZone`
     */
    scheduleType:
      campaign?.schedulerMsgParams?.scheduleType === ScheduleType.RecipientTimeZone
        ? ScheduleType.RecipientTimeZone
        : ScheduleType.ProjectTimeZone,
    skipOnDays,
    recurrence,
    precreateHours:
      campaign?.schedulerMsgParams?.precreateHours?.toString() ??
      DEFAULT_PRE_CREATE_RECURRING_CAMPAIGN_HOURS.toString(),
    recurrenceFrequency:
      campaign?.schedulerMsgParams?.recurDays?.toString() ??
      DEFAULT_CAMPAIGN_RECURRENCE_FREQUENCY.toString(),
  };
};
